import { z } from "zod";
/**
 * WhatsApp message templates — the one definition of a valid template, shared by
 * the CRM form and the route body so the two cannot drift.
 *
 * This is a Pattern-B schema (like `catalogPayloadSchema`): it is `safeParse`d in
 * the browser to render inline errors AND attached as the Fastify body schema.
 * Every rule below is one Meta enforces; validating here means an admin sees the
 * problem while typing instead of as a rejection three hours later.
 */
// Meta: "Template names can only contain lowercase alphanumeric characters and
// underscores (regex: ^[a-z0-9_]+$)." Their cap is 512; 200 is past anything a
// human types and keeps the column narrow.
export const templateNameSchema = z
    .string()
    .trim()
    .min(1, "El nombre es obligatorio")
    .max(200, "Máximo 200 caracteres")
    .regex(/^[a-z0-9_]+$/, "Sólo minúsculas, números y guiones bajos (por ejemplo: recordatorio_visita)");
// A Meta locale: "es_MX", "en", "pt_BR". The pilot only uses es_MX, but the field
// is real from day one — identity is name + language, so retrofitting it later is
// a migration on live rows.
export const templateLanguageSchema = z
    .string()
    .trim()
    .regex(/^[a-z]{2}(_[A-Z]{2})?$/, "Formato de idioma inválido (por ejemplo: es_MX)");
// Meta's three categories. AUTHENTICATION is accepted but not offered in the CRM
// picker — see `AUTHORABLE_TEMPLATE_CATEGORIES`.
export const templateCategorySchema = z.enum([
    "MARKETING",
    "UTILITY",
    "AUTHENTICATION",
]);
/**
 * The header formats we author. Meta also has VIDEO and LOCATION.
 *
 * `TEXT` is the absence of media, not a third media kind — which is why the
 * send-side `kind` below has only two members.
 */
export const templateHeaderFormatSchema = z.enum(["TEXT", "IMAGE", "DOCUMENT"]);
/** A stored media asset's kind. Mirrors the two non-TEXT header formats. */
export const templateMediaKindSchema = z.enum(["image", "document"]);
/** The header format a media kind produces, and the inverse. */
export function mediaKindForHeaderFormat(format) {
    if (format === "IMAGE")
        return "image";
    if (format === "DOCUMENT")
        return "document";
    return null;
}
/**
 * What Meta accepts for a template media header, and what WE accept.
 *
 * The MIME lists are Meta's own allowlist for the Resumable Upload API, so they
 * are not ours to widen. The size caps are deliberately DIFFERENT from Meta's:
 * Meta allows 5 MB for an image and 100 MB for a PDF, but a send buffers the
 * whole file in memory and Render's starter tier has ~512 MB — so the PDF cap is
 * ours and much lower. One shared cap would be wrong in both directions: at 5 MB
 * it rejects legitimate brochures, at 16 MB it lets through an image Meta refuses
 * with a Graph error the admin cannot read.
 *
 * Served to the CRM rather than hardcoded there — `DocumentsManager.tsx` keeps
 * its own copy of a cap and that is the mistake not to repeat.
 */
export const TEMPLATE_MEDIA_LIMITS = {
    image: {
        mimeTypes: ["image/jpeg", "image/jpg", "image/png"],
        /** Meta's own ceiling. Raising ours past it just moves the rejection later. */
        maxSizeMb: 5,
        accept: "image/jpeg,image/png",
    },
    document: {
        mimeTypes: ["application/pdf"],
        maxSizeMb: 16,
        accept: "application/pdf",
    },
};
/**
 * The categories the CRM editor OFFERS, as opposed to the ones it accepts.
 *
 * AUTHENTICATION is missing on purpose: it has its own component rules (OTP
 * buttons) this phase does not build. It stays in `templateCategorySchema`
 * because Meta can hand us one through the reconcile sweep, and a row we cannot
 * store is worse than one we cannot author.
 *
 * The picker iterates this — never two hardcoded options in JSX, or the reason
 * for the omission has nowhere to live.
 */
export const AUTHORABLE_TEMPLATE_CATEGORIES = [
    "MARKETING",
    "UTILITY",
];
/**
 * The category, in Spanish, for display.
 *
 * Takes `string` rather than `TemplateCategory` because `category` is mirrored
 * as `VarChar` over a Meta vocabulary that grows, so an unknown value must
 * render readably instead of blank — the `estimateBroadcastCost` discipline:
 * degrade, never throw. Four surfaces render this; none may spell it itself, and
 * none may print Meta's English enum into Spanish copy.
 */
export function templateCategoryLabel(category) {
    switch (category) {
        case "MARKETING":
            return "marketing";
        case "UTILITY":
            return "utilidad";
        case "AUTHENTICATION":
            return "autenticación";
        default:
            return category.toLowerCase();
    }
}
/**
 * Every `{{token}}` in the text, in order of first appearance, de-duplicated.
 *
 * The ONE regex over template variables — `extractPlaceholders` is built on it.
 * Repeating a variable is legal and useful in a named template: two appearances
 * of `{{nombre}}` share one example, which is why this de-duplicates.
 */
export function extractVariables(text) {
    const found = [];
    for (const match of text.matchAll(/\{\{\s*([^{}]+?)\s*\}\}/g)) {
        const token = match[1];
        if (token !== undefined && !found.includes(token))
            found.push(token);
    }
    return found;
}
/**
 * Meta: a named parameter is a "unique, single string, composed of lowercase
 * characters and underscores". Digits are accepted in practice (`fecha_2`); the
 * 40-char cap is ours, to keep the insert chips and the examples table legible.
 */
export const templateVariableNameSchema = z
    .string()
    .max(40, "Máximo 40 caracteres")
    .regex(/^[a-z][a-z0-9_]*$/, "El nombre del dato sólo admite minúsculas sin acentos, números y guiones bajos");
/**
 * POSITIONAL when every token is a number, NAMED when none is. Text with no
 * variables is POSITIONAL — Meta's own default, and with nothing to interpolate
 * the choice is inert either way.
 */
export function parameterFormat(tokens) {
    if (tokens.length === 0)
        return "POSITIONAL";
    return tokens.every((t) => /^\d+$/.test(t)) ? "POSITIONAL" : "NAMED";
}
/** True when the text mixes `{{1}}` with `{{nombre}}`. Meta accepts neither half. */
export function hasMixedParameterFormats(tokens) {
    const numeric = tokens.filter((t) => /^\d+$/.test(t)).length;
    return numeric > 0 && numeric < tokens.length;
}
/**
 * "Nombre del alumno" → "nombre_del_alumno".
 *
 * Lives next to `templateVariableNameSchema` on purpose: the CRM's insert button
 * slugifies what the admin typed and the schema then validates it, so the two
 * have to encode the same rule or the button produces something Zod rejects.
 */
export function slugifyVariableName(label) {
    return label
        .normalize("NFD")
        // Strip the combining accents NFD just separated out — Meta allows no
        // diacritics, and "interés" has to become "interes", not "inter_s".
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
}
/** The presentable inverse: "nombre_del_alumno" → "Nombre del alumno". */
export function variableLabel(name) {
    const words = name.replace(/_+/g, " ").trim();
    return words === "" ? name : words.charAt(0).toUpperCase() + words.slice(1);
}
/**
 * The positional placeholders, ascending.
 *
 * Meta requires an `example` for every variable, and a missing one is the single
 * most common cause of rejection.
 */
export function extractPlaceholders(text) {
    return extractVariables(text)
        .filter((t) => /^\d+$/.test(t))
        .map(Number)
        .sort((a, b) => a - b);
}
/**
 * True when the placeholders are 1..n with no gaps. Meta rejects `{{1}} {{3}}`,
 * and the failure text does not explain why, so it is caught here.
 */
export function placeholdersAreSequential(placeholders) {
    return placeholders.every((n, i) => n === i + 1);
}
const exampleValueSchema = z
    .string()
    .trim()
    .min(1, "El ejemplo es obligatorio")
    .max(120, "Máximo 120 caracteres")
    // Meta rejects examples containing newlines, tabs or 4+ consecutive spaces.
    .regex(/^[^\n\t\r]*$/, "El ejemplo no puede tener saltos de línea")
    .refine((v) => !/ {4,}/.test(v), "El ejemplo no puede tener 4 o más espacios seguidos");
/**
 * The authoring payload. Deliberately NOT Meta's `components` array: the admin
 * fills a form and the backend assembles the JSON, so invalid component JSON is
 * impossible rather than merely rejected. Buttons are out of scope this phase —
 * they carry their own validation rules and triple the editor.
 */
const templateContentShape = {
    /**
     * Which kind of header the template carries.
     *
     * Sibling fields rather than a discriminated union on `header`, deliberately:
     * `header` keeps meaning "the TEXT header", so every existing reader — the
     * DTO, `TemplatePreview`'s three call sites, `refineContent`'s inline
     * structural signature, and the `header: null` fixtures in five test files —
     * is untouched by adding media. VIDEO and LOCATION are Meta's other options
     * and are out of scope: each carries its own preview and size rules.
     */
    headerFormat: templateHeaderFormatSchema.default("TEXT"),
    /**
     * The uploaded asset backing a media header (`waba_media_assets.id`).
     *
     * Meta reviews a SAMPLE file when it approves the template, so one is
     * mandatory at authoring time — see the refinement below.
     */
    headerMediaAssetId: z.string().uuid().nullable().default(null),
    // The TEXT header's text. Null on a media header, enforced below.
    header: z.string().trim().max(60, "Máximo 60 caracteres").nullable().default(null),
    body: z
        .string()
        .trim()
        .min(1, "El cuerpo del mensaje es obligatorio")
        .max(1024, "Máximo 1024 caracteres"),
    footer: z.string().trim().max(60, "Máximo 60 caracteres").nullable().default(null),
    // One example per placeholder, in placeholder order.
    bodyExamples: z.array(exampleValueSchema).max(20).default([]),
};
function refineContent(value, ctx) {
    if (value.headerFormat === "TEXT") {
        // A TEXT header admits no variable: it would need its own example array,
        // which Meta keys separately from the body's. Rejected rather than silently
        // dropped.
        if (value.header && extractVariables(value.header).length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "El encabezado no admite variables en esta versión",
                path: ["header"],
            });
        }
        if (value.headerMediaAssetId !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Un encabezado de texto no lleva archivo",
                path: ["headerMediaAssetId"],
            });
        }
    }
    else {
        // Meta needs a sample file to review, so the asset is not optional — and it
        // is the same slot the send reuses by default.
        if (value.headerMediaAssetId === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: value.headerFormat === "IMAGE"
                    ? "Sube la imagen del encabezado"
                    : "Sube el documento del encabezado",
                path: ["headerMediaAssetId"],
            });
        }
        // Meta's header component is text OR media, never both.
        if (value.header !== null && value.header !== "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Un encabezado con archivo no lleva texto",
                path: ["header"],
            });
        }
    }
    const variables = extractVariables(value.body);
    // A body Meta refuses whole: it reads one format per template, so half of
    // these placeholders would never be filled in.
    if (hasMixedParameterFormats(variables)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "No mezcles datos numerados ({{1}}) con datos con nombre ({{nombre}}) en el mismo mensaje",
            path: ["body"],
        });
        return;
    }
    if (parameterFormat(variables) === "POSITIONAL") {
        if (!placeholdersAreSequential(extractPlaceholders(value.body))) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Las variables deben ir numeradas desde {{1}} y sin saltos",
                path: ["body"],
            });
            return;
        }
    }
    else {
        // The name is what Meta stores and what the send payload keys on, so an
        // invalid one is a rejection later; naming the offender makes it fixable.
        for (const name of variables) {
            const parsed = templateVariableNameSchema.safeParse(name);
            if (!parsed.success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `El dato "${name}" no es válido: ${parsed.error.issues[0]?.message ?? "nombre inválido"}`,
                    path: ["body"],
                });
                return;
            }
        }
    }
    // Meta rejects a body that is nothing but a variable, and one that starts or
    // ends with one — there has to be text around it.
    const stripped = value.body.replace(/\{\{\s*[^{}]+?\s*\}\}/g, "").trim();
    if (variables.length > 0 && stripped === "") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "El cuerpo no puede ser sólo variables",
            path: ["body"],
        });
    }
    if (value.bodyExamples.length !== variables.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: variables.length === 0
                ? "Este mensaje no tiene variables, así que no lleva ejemplos"
                : `Faltan ejemplos: hay ${variables.length} variable(s) y ${value.bodyExamples.length} ejemplo(s)`,
            path: ["bodyExamples"],
        });
    }
}
export const createTemplateSchema = z
    .object({
    name: templateNameSchema,
    language: templateLanguageSchema.default("es_MX"),
    category: templateCategorySchema,
    ...templateContentShape,
})
    .superRefine(refineContent);
// Name and language are the template's identity in Meta and cannot be edited on
// a submitted template, so they are absent here rather than accepted-and-ignored.
export const updateTemplateSchema = z
    .object({
    category: templateCategorySchema,
    ...templateContentShape,
})
    .superRefine(refineContent);
export const listTemplatesQuerySchema = z.object({
    includeArchived: z.coerce.boolean().default(false),
});
/**
 * What each category costs to send, for the cost note in the template editor.
 *
 * ONE source, never a price written into JSX: these rates are per-country and
 * moving. The editor's picker is a REQUEST, not a decision — Meta recategorizes
 * approved templates on its own and category IS the price — so `requestedCategory`
 * records what the admin asked for, `category` is what Meta billed it as, and both
 * rates have to be on screen while they write.
 */
export const TEMPLATE_CATEGORY_PRICING = {
    currency: "MXN",
    country: "México",
    // The date these figures were taken from Meta's rate card. Shown in the UI so a
    // stale number is visibly stale instead of quietly wrong.
    asOf: "2026-09",
    rates: {
        MARKETING: 0.5614,
        UTILITY: 0.1565,
        AUTHENTICATION: 0.1565,
    },
};
/**
 * One template parameter, cleaned for Meta's send endpoint.
 *
 * Lives beside `exampleValueSchema` because it encodes the SAME Meta rule that
 * schema already encodes for examples — newlines, tabs and 4+ consecutive spaces
 * are rejected — except this one runs over values we interpolate at send time,
 * where rejecting is not an option: the value came from a lead row, not from a
 * form someone can fix. So it repairs rather than refuses.
 *
 * Sanitize BEFORE testing for empty. A `leads.name` of nothing but spaces has to
 * read as "we don't have this", not as a space delivered to 500 people.
 */
export function sanitizeTemplateParameter(raw) {
    return (raw ?? "")
        .replace(/[\n\t\r]+/g, " ")
        .replace(/ {2,}/g, " ")
        .trim()
        // Meta's own cap is 1024 for a body parameter. Truncating keeps one runaway
        // value from failing the whole send.
        .slice(0, 1024);
}
/**
 * What a broadcast of `recipients` messages will cost at Meta's current rate.
 *
 * ONE implementation, imported by both the backend and the CRM: two
 * implementations of a price are two prices, and this number is what a school
 * approves a spend against.
 *
 * Callers must pass `template.category` — what Meta currently BILLS it as —
 * never `requestedCategory`. Meta recategorizes approved templates on its own,
 * and category IS the price (~3.5x between marketing and utility in Mexico), so
 * quoting the requested one under-quotes by that factor the moment they differ.
 *
 * An unknown category returns 0 and never throws — the `MODEL_PRICING_REGISTRY`
 * rule. A price registry that throws takes the feature down the day Meta adds a
 * category; the caller renders "no podemos estimar" and still allows the send.
 */
export function estimateBroadcastCost(category, recipients) {
    const rates = TEMPLATE_CATEGORY_PRICING.rates;
    const unitCost = rates[category];
    const known = unitCost !== undefined;
    const rate = known ? unitCost : 0;
    return {
        currency: TEMPLATE_CATEGORY_PRICING.currency,
        unitCost: rate,
        recipients,
        // Rounded to the same 4 decimals the rate carries, so the number the UI
        // shows and the number the DB stores agree exactly.
        total: Math.round(rate * recipients * 10_000) / 10_000,
        category,
        knownCategory: known,
        asOf: TEMPLATE_CATEGORY_PRICING.asOf,
    };
}
/**
 * The per-message RATE, at four decimals.
 *
 * Deliberately not `format-mxn.ts`, which takes integer centavos at two decimals
 * and would render MXN 0.5614 as "$0.56" — destroying the precision the rate
 * actually has. And deliberately split from `formatTemplateTotal` below: four
 * decimals on a sum is noise ("$280.7000"). Do not "harmonize" them back.
 */
export function formatTemplateRate(amount, currency) {
    return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency,
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
    }).format(amount);
}
/** The TOTAL, at two decimals. See the note on `formatTemplateRate`. */
export function formatTemplateTotal(amount, currency) {
    return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}
//# sourceMappingURL=waba-templates.js.map